import { Card } from "@design";
import { Workflow } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ToolConfirmationDecision } from "@/api";
import { Text } from "@/components/Text";
import { ActionChoice } from "@/features/chat/actions/ActionChoice";
import { ActionSummary } from "@/features/chat/actions/ActionSummary";
import { type ActionProposal, describeAction } from "@/features/chat/actions/actionModel";
import { ProposalProvider } from "@/features/chat/research/ProposalContext";
import { formatRelativeTime } from "@/i18n/format";
import type { ActionRequest } from "./useApprovals";

/**
 * One write action a flow run paused for (MHS-972): the flow and run it came from, what it will do, where it goes and
 * what it sends, with the same choice as in chat (Approve, Edit, Don't do it), plus "Always allow when I run this workflow"
 * (MHS-972) when it names where it goes: later runs then take it without pausing (editors, if the org allows).
 */
export function ApprovalItem({
  request,
  onDecide,
}: Readonly<{
  request: ActionRequest;
  onDecide: (decision: ToolConfirmationDecision, onFailed: () => void) => void;
}>) {
  const { t } = useTranslation("workflows", { keyPrefix: "approvals" });
  const [editing, setEditing] = useState(false);
  const action: ActionProposal = useMemo(
    () => ({
      ...describeAction(request.toolName, request.arguments, request.details),
      id: request.uuid,
      status: "pending",
      remembered: false,
      receipt: null,
    }),
    [request],
  );
  const decisions = useMemo(
    () => ({
      readOnly: false,
      busy: false,
      // Only decisions come from here: the inbox has no cards of its own that send a message.
      onDecide: (_label: string, d: ToolConfirmationDecision | undefined, f: () => void) => {
        if (d) onDecide(d, f);
      },
    }),
    [onDecide],
  );
  const runHref =
    request.flowUuid && request.flowRunUuid ? `/build/workflows/${request.flowUuid}/runs/${request.flowRunUuid}` : null;
  const flowName = request.flowName ?? t("unknownFlow");
  return (
    <Card>
      <article aria-label={t("itemLabel", { flow: flowName })} className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Workflow size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
          {runHref ? (
            <Link to={runHref} className="min-w-0 break-words text-content-primary underline-offset-2 hover:underline">
              <Text as="span" variant="body-sm">
                {flowName}
              </Text>
            </Link>
          ) : (
            <Text as="span" variant="body-sm">
              {flowName}
            </Text>
          )}
          <Text as="span" variant="caption" tone="tertiary">
            {request.expiresAt
              ? t("waitingUntil", {
                  since: formatRelativeTime(request.createdAt),
                  expires: formatRelativeTime(request.expiresAt),
                })
              : t("waitingSince", { since: formatRelativeTime(request.createdAt) })}
          </Text>
        </div>
        <ActionSummary action={action} omit={editing ? Object.keys(action.editable) : []} />
        <ProposalProvider value={decisions}>
          <ActionChoice action={action} editing={editing} onEditing={setEditing} rememberLabel={t("alwaysAllow")} />
        </ProposalProvider>
      </article>
    </Card>
  );
}
